export type PrivilegeMap = Record<string, boolean>;

const normalizeSegment = (value: string): string => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .replace(/[\s-]+/g, '_')
  .toLowerCase();

export const normalizePrivilegeKey = (value: string): string => value
  .split('.')
  .map(normalizeSegment)
  .filter(Boolean)
  .join('.');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const addKey = (result: PrivilegeMap, key: string): void => {
  const normalized = normalizePrivilegeKey(key);
  if (normalized) result[normalized] = true;
};

const visit = (value: unknown, result: PrivilegeMap, prefix = ''): void => {
  if (typeof value === 'string') {
    addKey(result, prefix ? `${prefix}.${value}` : value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (typeof item === 'string') {
        addKey(result, prefix ? `${prefix}.${item}` : item);
      } else if (isRecord(item)) {
        const itemKey = typeof item['key'] === 'string'
          ? item['key']
          : typeof item['privilege'] === 'string'
            ? item['privilege']
            : undefined;

        if (itemKey && item['enabled'] !== false) {
          addKey(result, prefix ? `${prefix}.${itemKey}` : itemKey);
        }

        visit(item['privileges'], result, prefix);
        visit(item['actions'], result, prefix);
      }
    });
    return;
  }

  if (!isRecord(value)) return;

  const objectKey = typeof value['key'] === 'string'
    ? value['key']
    : typeof value['privilege'] === 'string'
      ? value['privilege']
      : undefined;
  if (objectKey && value['enabled'] !== false) {
    addKey(result, prefix ? `${prefix}.${objectKey}` : objectKey);
  }

  Object.entries(value).forEach(([key, child]) => {
    if (['key', 'privilege', 'enabled', 'name', 'description', 'id'].includes(key)) return;

    const nextPrefix = key === 'privileges' || key === 'actions'
      ? prefix
      : prefix
        ? `${prefix}.${key}`
        : key;

    if (child === true) {
      addKey(result, nextPrefix);
      return;
    }

    if (child !== false && child !== null && child !== undefined) {
      visit(child, result, nextPrefix);
    }
  });
};

export const normalizePrivilegeMap = (value: unknown): PrivilegeMap => {
  const result: PrivilegeMap = {};
  visit(value, result);
  return result;
};

export const hasPrivileges = (value: unknown): boolean =>
  Object.keys(normalizePrivilegeMap(value)).length > 0;
